import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { ProjectOnList, ProjectSortOptions } from '@/api/v4/projects.api';
import { useHandleFilters } from '@/components/filters/use-handle-filters';
import { FixedBottomContainer } from '@/components/fixed-bottom-container/fixed-bottom-container';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { InfiniteScrollTable } from '@/ui/table/infinite-table/infinite-scroll-table';
import { NoResults } from '@/ui/table/no-results';
import type { TabConfig } from '@/ui/tabs/tabs';
import { Tabs } from '@/ui/tabs/tabs';
import type { ISort } from '@/ui/table/table.types';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { useAccountSwitchNotificationFromUrl } from '@/features/account/hooks/use-account-switch-notification-from-url';

import { WithHtmlSavvyTitle } from '../../with-html-savvy-title';
import { ProjectsFilters } from '@/features/projects/filters/projects-filters';
import { useProjectsFiltersSearchLocationSearch } from '@/features/projects/filters/use-projects-filters-location-search';
import type { IProjectFilters } from '@/features/projects/project.state';
import {
  appliedProjectsFiltersState,
  selectedProjectsFiltersState,
  projectsSortState,
} from '@/features/projects/project.state';
import { useProjectsTableConfiguration } from '@/features/projects/projects-list/use-projects-table-configuration';
import { useProjects } from '@/features/projects/projects-list/use-projects.query';
import { usePendingProjectRequests } from '@/features/projects/projects-list/use-pending-project-requests.query';
import { usePendingProjectRequestsCount } from '@/features/projects/projects-list/use-pending-project-requests-count.query';
import { PendingRequestsInfiniteTable } from '@/features/projects/projects-list/components/pending-requests-infinite-table';
import { TOPBAR_HEIGHT } from '@/features/topbar/topbar';
import { ProjectsKPI } from './projects-kpi/projects-kpi';
import { TableWrapper } from '@/features/projects/projects-list/table-wrapper';
import { PendingProjectsEmptyState } from '@/features/projects/projects-list/pending-projects-empty-state';
import {
  useProjectsTabParam,
  type ProjectTabs,
} from '@/features/projects/projects-list/use-projects-tab-param';
import { useAtomValue } from 'jotai';
import { NotificationsSidebar } from '../notifications/notifications-sidebar';

/**
 * Projects page with URL-based tab state management.
 *
 * URL patterns:
 * - /projects (defaults to allProjects tab)
 * - /projects?activeTab=allProjects
 * - /projects?activeTab=pendingProjects
 *
 * Usage:
 * - navigateTo.projectsWithTab('pendingProjects')
 * - Direct URL access: /projects?activeTab=pendingProjects
 */
export const ProjectsPage = () => {
  const { t } = useTranslation('projects');
  useAccountSwitchNotificationFromUrl();
  const navigateTo = useNavigateTo();
  const { hasRequiredPermission } = useUserPermissions();
  const hasEditPermission = hasRequiredPermission(PERMISSION.ADD_PROJECT);
  const canViewPendingRequests = hasRequiredPermission(
    PERMISSION.VIEW_PENDING_PROJECT_REQUESTS,
  );
  const kpisContainerRef = useRef<HTMLDivElement>(null);
  const appliedFilters = useAtomValue(appliedProjectsFiltersState);
  const { data } = useProjects();
  const projects = data ? data.pages.flat() : [];

  const { tabParam, setTabParam, deleteTabParam } = useProjectsTabParam();

  const getValidTabWithPermissions = (tab: ProjectTabs | null): ProjectTabs => {
    if (tab === 'allProjects') {
      return tab;
    }
    if (tab === 'pendingProjects' && canViewPendingRequests) {
      return tab;
    }
    return 'allProjects';
  };

  const [activeTab, setActiveTab] = useState<ProjectTabs>(() =>
    getValidTabWithPermissions(tabParam),
  );

  const {
    hasNextPage: projectsHasNextPage,
    isLoading: projectsIsLoading,
    fetchNextPage: projectsFetchNextPage,
  } = useProjects();
  const {
    data: pendingRequestsData,
    isLoading: pendingRequestsIsLoading,
    fetchNextPage: pendingRequestsFetchNextPage,
    hasNextPage: pendingRequestsHasNextPage,
  } = usePendingProjectRequests({
    enabled: canViewPendingRequests,
  });
  const { data: pendingRequestsCountData } = usePendingProjectRequestsCount({
    enabled: canViewPendingRequests,
  });

  const pendingRequests = pendingRequestsData
    ? pendingRequestsData.pages.flat()
    : [];

  const {
    header: projectsHeader,
    rows: projectsRows,
    skeletonConfig: projectsSkeletonConfig,
  } = useProjectsTableConfiguration(projects);

  const { updateLocationSearch, resetLocationSearch } =
    useProjectsFiltersSearchLocationSearch();

  const { clearAppliedFilters, hasAnyActiveFilters } =
    useHandleFilters<IProjectFilters>({
      appliedFiltersAtom: appliedProjectsFiltersState,
      selectedFiltersAtom: selectedProjectsFiltersState,
      updateLocationSearch,
      resetLocationSearch,
    });

  // Sync tab state with URL parameter
  useEffect(() => {
    const validTab = getValidTabWithPermissions(tabParam);
    if (validTab !== activeTab) {
      setActiveTab(validTab);
    }
  }, [tabParam, activeTab, canViewPendingRequests]);

  const handleTabChange = (tab: ProjectTabs) => {
    setActiveTab(tab);
    if (tab === 'allProjects') {
      deleteTabParam();
    } else {
      setTabParam(tab);
    }
  };

  const handleSort = ({ sort, direction }: ISort<ProjectSortOptions>) =>
    updateLocationSearch(appliedFilters, { direction, sort });

  const onCreateProject = () => {
    navigateTo.createProject();
  };

  const allProjectsTab: TabConfig<ProjectTabs> = {
    id: 'allProjects' as const,
    title: (
      <TabTitle>
        {t`projects.tabs.allProjects`}{' '}
        {projects.length < 10 && projects.length > 0
          ? `0${projects.length}`
          : projects.length}
      </TabTitle>
    ),
    content: (
      <>
        {!projectsIsLoading && projectsRows.length === 0 ? (
          <Flexbox
            name="projects-no-results-wrapper"
            justify="center"
            alignItems="center"
            fullHeight
          >
            {hasAnyActiveFilters ? (
              <NoResults
                header={t`projects.noResults.header`}
                text={t`projects.noResults.text`}
                buttonText={t`projects.noResults.button`}
                buttonIcon="ArrowGoBack"
                onButtonClick={clearAppliedFilters}
                marginTop="56px"
              />
            ) : (
              <NoResults
                onButtonClick={onCreateProject}
                buttonText={t`projects.createProject`}
                header={t`projects.empty.title`}
                text={hasEditPermission ? t`projects.empty.description` : ''}
                buttonIcon="Plus"
                showButton={hasEditPermission}
                marginTop="56px"
              />
            )}
          </Flexbox>
        ) : (
          <TableWrapper id="projects-table-scroll-container">
            <InfiniteScrollTable<ProjectOnList, ProjectSortOptions>
              id="projects"
              isLoading={projectsIsLoading}
              headers={projectsHeader}
              rows={projectsRows}
              data={projects}
              hasMore={Boolean(projectsHasNextPage)}
              onLoadMore={projectsFetchNextPage}
              onSort={handleSort}
              sortState={projectsSortState}
              height="100%"
              scrollableTarget="projects-table-scroll-container"
              skeletonConfig={projectsSkeletonConfig}
            />
          </TableWrapper>
        )}
      </>
    ),
  };

  const pendingProjectsTab: TabConfig<ProjectTabs> = {
    id: 'pendingProjects' as const,
    title: (
      <TabTitle>
        {t`projects.tabs.pendingProjects`}{' '}
        {(() => {
          const totalCount = pendingRequestsCountData?.count ?? 0;
          return totalCount < 10 && totalCount > 0
            ? `0${totalCount}`
            : totalCount;
        })()}
      </TabTitle>
    ),
    content: (
      <>
        {!pendingRequestsIsLoading && pendingRequests.length === 0 ? (
          <PendingProjectsEmptyState />
        ) : (
          <TableWrapper id="pending-requests-table-scroll-container">
            <PendingRequestsInfiniteTable
              pendingRequests={pendingRequests}
              isLoading={pendingRequestsIsLoading}
              hasMore={Boolean(pendingRequestsHasNextPage)}
              onLoadMore={pendingRequestsFetchNextPage}
              height="100%"
              scrollableTarget="pending-requests-table-scroll-container"
            />
          </TableWrapper>
        )}
      </>
    ),
  };

  const tabsConfig: TabConfig<ProjectTabs>[] = [
    allProjectsTab,
    ...(canViewPendingRequests ? [pendingProjectsTab] : []),
  ];

  return (
    <WithHtmlSavvyTitle title="Projects">
      <ProjectsContentContainer
        name="projects-page-container"
        direction="column"
        padding="0 30px"
        fullHeight
      >
        <ProjectsFilters />

        <TabContainer>
          <Tabs<ProjectTabs>
            tabsConfig={tabsConfig}
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            border
            width="max-content"
            noShadow
            padding="0 32px"
          />
        </TabContainer>

        <FixedBottomContainer>
          <div ref={kpisContainerRef}>
            <ProjectsKPI />
          </div>
        </FixedBottomContainer>
      </ProjectsContentContainer>
      <NotificationsSidebar />
    </WithHtmlSavvyTitle>
  );
};

const ProjectsContentContainer = styled(Flexbox)`
  position: relative;
  padding-bottom: 60px;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  height: calc(100vh - ${TOPBAR_HEIGHT});
`;

const TabContainer = styled('div')`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;

  /* Make Tabs component fill available space */
  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
  }
`;

const TabTitle = styled('span')`
  font-family: ${({ theme }) => theme.fonts.neue};
  padding: 6px 12px;
`;
